import { useMemo } from 'react';

import type { DeserializedAppData } from '@api';

import type { ReviewContentReviewData } from '@shared/api/types/review';
import { REVIEW_TYPE } from '@shared/api/types/review';
import { getProcessEnvGlobalConfig } from '@shared/config/global';
import formatUsername from '@shared/helpers/formatUsername';

import type { PlatformConfiguration } from '@store:web/components/common/ShareSocialsList/utils';

export default function useReviewShareConfig({
  app,
  review,
}: {
  app: Pick<DeserializedAppData['attributes'], 'appLink' | 'appName'>;
  review: ReviewContentReviewData;
}): { shareUrl: string; shareConfig: PlatformConfiguration } {
  return useMemo(() => {
    const userName = formatUsername(review.user.attributes);
    const projectName = app.appName;
    const url = `
      ${getProcessEnvGlobalConfig('host')}${getProcessEnvGlobalConfig('basePath')}/user/
      ${review.user.attributes.magicId}/
      ${review.type === REVIEW_TYPE.TRUST_REVIEW ? 'trust-review' : 'review'}/
      ${review.id}`;
    const config: PlatformConfiguration = {
      Reddit: {
        title: `✍️ Review of ${projectName} by ${userName} on Magic Store\n\n
        Hey Web3 people,\n\n
        🔥 Found this hot take by ${userName} on ${projectName} from Magic Store. Worth discussing!\n\n🔗 {{url}}`,
        message: `${'text'}`,
        url,
      },
      Email: {
        title: `🌐 Revealing Review on Magic Store`,
        message: `Hey,\n\n📖 Have you seen this? ${userName} penned down their thoughts about ${projectName} over at Magic Store.\n\n🔗 {{url}}\n\nBest,\n${userName}`,
        url,
      },
      Twitter: {
        message: `🔍 Review Alert: Find this ${userName}'s insights on ${projectName} from #MagicStore. Enlightening! 💡\n`,
        url,
      },
      Telegram: {
        message: `🚀 Find out what ${userName} has to say about ${projectName} on Magic Store! Eye-opening thoughts ahead.\n`,
        url,
      },
      WhatsApp: {
        message: `Hey! 👋 Check out this review by ${userName} about ${projectName} on Magic Store. Quite insightful! 🌟\n\nWhat are your thoughts?\n🔗 {{url}}`,
        url,
      },
      Facebook: {
        url,
      },
      LinkedIn: {
        url,
      },
    };

    return {
      shareUrl: url,
      shareConfig: config,
    };
  }, [review, app]);
}
